import { getUuidV4 } from '../../utilities/generic-utilities';

export const DEVICE_LOCK_KEY = 'pace-az-device-lock';
/**
 * Where the pre-Azure client kept its lock, in the same `LockValue` shape. Read only once, to
 * recognise a lock carried over from the Gateway; the Azure lock never writes here.
 */
export const LEGACY_DEVICE_LOCK_KEY = 'pace-device-lock';
export const DEVICE_LOCK_CLIENTID = 'pace-device-lock-id';
export const DEVICE_LOCK_CLIENTID_CREATED_AT = 'pace-device-lock-id-created-at';
export const DEVICE_LOCK_SYNCED_AT = 'pace-device-lock-synced-at';

/**
 * Raised when the device lock itself was acquired but the concurrent-usage download of this
 * acquisition could not be completed. Distinguished from lock-ownership failures so the UI
 * shows an initialization error instead of the (misleading) device lock screen.
 */
export class ConcurrentDataSyncError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = 'ConcurrentDataSyncError';
    }
}

/**
 * Marker for the lock acquisition (its acquiredAt) whose concurrent-usage download has
 * completed. Written only after a successful downloadAll, so an interrupted download
 * (e.g. page refresh mid-download) is retried on the next initialization.
 */
export function loadSyncedAt(): string | null {
    return localStorage.getItem(DEVICE_LOCK_SYNCED_AT);
}

export function saveSyncedAt(acquiredAt: string | null): void {
    if (acquiredAt) {
        localStorage.setItem(DEVICE_LOCK_SYNCED_AT, acquiredAt);
    } else {
        localStorage.removeItem(DEVICE_LOCK_SYNCED_AT);
    }
}

/**
 * Whether the first getOrCreateClientId call of this JS session had to generate a fresh id.
 * Module state on purpose: the interceptor usually resolves the id before DeviceLockService
 * is constructed, so the service cannot tell "generated just now" from "re-used" by looking
 * at localStorage alone.
 */
let clientIdGeneratedThisSession: boolean | null = null;

/**
 * Returns the stable per-browser client ID used as `x-pace-device-id` and for lock ownership,
 * creating and persisting a new one on first use. Kept outside DeviceLockService so the
 * HTTP interceptor can attach the header without depending on the service.
 */
export function getOrCreateClientId(): string {
    const storedClientId = localStorage.getItem(DEVICE_LOCK_CLIENTID);
    if (storedClientId) {
        clientIdGeneratedThisSession ??= false;
        return storedClientId;
    }

    const clientId = getUuidV4();
    localStorage.setItem(DEVICE_LOCK_CLIENTID, clientId);
    localStorage.setItem(DEVICE_LOCK_CLIENTID_CREATED_AT, String(Date.now()));
    clientIdGeneratedThisSession = true;
    return clientId;
}

/** True when this session's first getOrCreateClientId call generated the id. */
export function wasClientIdGeneratedThisSession(): boolean {
    return clientIdGeneratedThisSession === true;
}

/** Resets the session-scoped generation tracking. Only intended for unit tests. */
export function resetClientIdSessionState(): void {
    clientIdGeneratedThisSession = null;
}

/**
 * Fingerprint of the device for locking purposes.
 *
 * Not to be confused with a security fingerprint, this is just a collection
 * of some basic information to help identify the device that holds the lock.
 * The clientId is the relevant field, as it is used to determine lock ownership.
 * The other fields are informational and can be used to display information about the lock owner.
 */
export interface DeviceLockFingerprint {
    os?: string;
    vendor?: string;
    model?: string;
    browser?: string;
    standalone?: boolean;
    clientId: string;
    isPrivateBrowser: boolean;
}

export function prettifyDeviceLockFingerprint(fingerprint: DeviceLockFingerprint): string {
    const clientId = fingerprint.clientId?.toUpperCase();
    if (!clientId) {
        return 'no clientId';
    }
    return `${clientId.substring(0, 4)}-${clientId.substring(4, 8)}`;
}

export interface LockState {
    ownedByMe: boolean;
    ownerFingerprint: DeviceLockFingerprint | null;
    acquiredAt: string | null;
}

export const INITIAL_LOCK_STATE: LockState = {
    ownedByMe: false,
    ownerFingerprint: null,
    acquiredAt: null,
};

export interface LockValue {
    fingerprint: DeviceLockFingerprint | null;
    acquiredAt: string | null;
}

/** Device metadata as stored by the Azure Functions device-lock API. */
export interface DeviceLockRemoteDeviceInfo {
    os?: string;
    vendor?: string;
    model?: string;
    browser?: string;
    isPrivateBrowser: boolean;
}

/** Body of POST v1/device-lock/acquire and force-acquire. */
export interface DeviceLockAcquireRequestBody {
    os?: string;
    vendor?: string;
    model?: string;
    browser?: string;
    isPrivateBrowser: boolean;
}

/** Response of GET v1/device-lock. Owner fields are absent when no lock exists. */
export interface DeviceLockStateResponse {
    locked: boolean;
    deviceId?: string;
    device?: DeviceLockRemoteDeviceInfo;
    acquiredAt?: string;
    lastSeenAt?: string;
    forced?: boolean;
}

/** Response of POST v1/device-lock/acquire and force-acquire. */
export interface DeviceLockAcquireResponse {
    acquired: boolean;
    message?: string;
    generation?: number;
    acquiredAt?: string;
    deviceId?: string;
    /** The lock is the unchanged copy of the pre-Azure Gateway lock. */
    migratedFromGateway?: boolean;
}

/** 423 error body enriched with the current lock holder. */
export interface DeviceLockHeldError {
    message: string;
    code?: string;
    requestId?: string;
    lock?: {
        deviceId: string;
        device?: DeviceLockRemoteDeviceInfo;
        acquiredAt?: string;
        lastSeenAt?: string;
    };
}

/**
 * Maps a remote lock holder (deviceId + device metadata) into the local LockValue shape.
 * The backend's deviceId is the holder's clientId — ownership comparisons rely on it.
 */
export function remoteHolderToLockValue(
    deviceId: string,
    device: DeviceLockRemoteDeviceInfo | undefined,
    acquiredAt: string | null,
): LockValue {
    return {
        fingerprint: {
            clientId: deviceId,
            os: device?.os,
            vendor: device?.vendor,
            model: device?.model,
            browser: device?.browser,
            isPrivateBrowser: device?.isPrivateBrowser ?? false,
        },
        acquiredAt,
    };
}

export const EMPTY_DEVICE_LOCK: LockValue = {
    fingerprint: null,
    acquiredAt: null,
};

export function lockValueToState(value: LockValue | null | undefined, myFingerprint: DeviceLockFingerprint): LockState {
    return {
        ownedByMe: value?.fingerprint?.clientId === myFingerprint.clientId,
        ownerFingerprint: value?.fingerprint ?? null,
        acquiredAt: value?.acquiredAt ?? null,
    };
}

/**
 * The device finger print consist of a random UUID (clientId) and a set of deterministic properties, such as browser,
 * os, vendor and model. This helper function compares two fingerprints.
 *
 * @param state
 * @param myFingerprint
 * @param fingerprintA
 * @param fingerprintB
 */
export function isDeterministicFingerprintEqual(
    fingerprintA: DeviceLockFingerprint,
    fingerprintB: DeviceLockFingerprint,
): boolean {
    return (
        fingerprintA.browser === fingerprintB.browser &&
        fingerprintA.os === fingerprintB.os &&
        fingerprintA.model === fingerprintB.model &&
        fingerprintA.vendor === fingerprintB.vendor
    );
}

export function loadLocalLock(): LockValue | null {
    try {
        return JSON.parse(localStorage.getItem(DEVICE_LOCK_KEY) ?? 'null');
    } catch {
        return null;
    }
}

export function saveLocalLock(lock: LockValue | null): void {
    if (lock) {
        localStorage.setItem(DEVICE_LOCK_KEY, JSON.stringify(lock));
    } else {
        localStorage.removeItem(DEVICE_LOCK_KEY);
    }
}

export function loadLegacyLocalLock(): LockValue | undefined {
    try {
        return JSON.parse(localStorage.getItem(LEGACY_DEVICE_LOCK_KEY) ?? 'null') ?? undefined;
    } catch {
        return undefined;
    }
}

export function removeLegacyLocalLock(): void {
    localStorage.removeItem(LEGACY_DEVICE_LOCK_KEY);
}
